import { mediaRef } from '@/services/mediaAssets';
import { galleryBySlug } from '../../../adventureGalleries';
import { defineReckonEvent } from '../../schema';

const barbicanCentreImage = mediaRef('adventures/london-barbican-centre.jpg');
const barrelProjectImage = mediaRef('adventures/london-barrel-project.jpg');
const brazenHeadImage = mediaRef('adventures/dublin-brazen-head.jpg');
const culpeperImage = mediaRef('adventures/london-the-culpeper.jpg');
const dublinSideQuestImage = mediaRef('adventures/dublin-side-quest.jpg');
const doodleBarImage = mediaRef('adventures/london-doodle-bar.jpg');
const fabalBeerhallImage = mediaRef('adventures/london-fabal-beerhall.jpg');
const georgeAndVultureImage = mediaRef('adventures/london-george-and-vulture.jpg');
const guinnessStorehouseImage = mediaRef('adventures/dublin-guinness-storehouse.jpg');
const theRelianceImage = mediaRef('adventures/london-the-reliance.jpg');
const henryLauVillageUndergroundImage = mediaRef('adventures/london-henry-lau-village-underground.png');
const idrisElbaSessionQueueImage = mediaRef('adventures/london-idris-elba-session-queue.jpg');
const pouletBeerImage = mediaRef('adventures/london-poulet.jpg');
const pouletChickenImage = mediaRef('adventures/london-poulet-chicken.jpg');
const queensHeadImage = mediaRef('adventures/london-queens-head.jpg');
const sxswLondonSignImage = mediaRef('adventures/london-sxsw-sign.jpg');
const templeBarImage = mediaRef('adventures/dublin-temple-bar.jpg');
const curzonHoxtonExteriorImage = mediaRef('adventures/london-curzon-hoxton.jpg');
const lateShiftQaImage = mediaRef('adventures/london-late-shift-qa.jpg');
const theKsHoxtonHallImage = mediaRef('adventures/london-the-ks-hoxton-hall.jpg');
const towerBridgeWalkImage = mediaRef('adventures/london-tower-bridge-walk.jpg');
const xoyoDjImage = mediaRef('adventures/london-xoyo-1.jpg');
const xoyoWyclefImage = mediaRef('adventures/london-xoyo-2.jpg');
const londonFlightArrivalImage = mediaRef('adventures/london-flight-arrival.jpg');
const londonSxswEntryImage = mediaRef('adventures/london-sxsw-entry.jpg');
const londonStreetArtPinkBearImage = mediaRef('adventures/london-street-art-pink-bear.jpg');
const londonSxswMapImage = mediaRef('adventures/london-sxsw-map.jpg');
const londonXoyoEntranceImage = mediaRef('adventures/london-xoyo-entrance.jpg');
const londonTowerBridgeReckonBottleImage = mediaRef('adventures/london-tower-bridge-reckon-bottle.jpg');
const londonDoodleBarSignImage = mediaRef('adventures/london-doodle-bar-sign.jpg');
const londonRedPhoneBoxImage = mediaRef('adventures/london-red-phone-box.jpg');
const londonBarrelProjectInteriorImage = mediaRef('adventures/london-barrel-project-interior.jpg');
const londonBermondseyFlagsImage = mediaRef('adventures/london-bermondsey-flags.jpg');
const londonPouletDiningRoomImage = mediaRef('adventures/london-poulet-dining-room.jpg');
const londonTowerBridgeNightImage = mediaRef('adventures/london-tower-bridge-night.jpg');
const londonTowerBridgePanoramaImage = mediaRef('adventures/london-tower-bridge-panorama.jpg');
const londonVideoJune2Morning = mediaRef('adventures/london-video-2025-06-02-095102.mp4', {
  firebaseAssetId: 'london-video-june-2-morning',
});
const londonVideoJune2Afternoon = mediaRef('adventures/london-video-2025-06-02-153154.mp4', {
  firebaseAssetId: 'london-video-june-2-afternoon',
});
const londonVideoJune4AfternoonOne = mediaRef('adventures/london-video-2025-06-04-141217.mp4', {
  firebaseAssetId: 'london-video-june-4-afternoon-one',
});
const londonVideoJune4AfternoonTwo = mediaRef('adventures/london-video-2025-06-04-143934.mp4', {
  firebaseAssetId: 'london-video-june-4-afternoon-two',
});
const londonVideoJune4Night = mediaRef('adventures/london-video-2025-06-04-221011.mp4', {
  firebaseAssetId: 'london-video-june-4-night',
});
const londonVideoJune5AfternoonOne = mediaRef('adventures/london-video-2025-06-05-142249.mp4', {
  firebaseAssetId: 'london-video-june-5-afternoon-one',
});
const londonVideoJune5AfternoonTwo = mediaRef('adventures/london-video-2025-06-05-152852.mp4', {
  firebaseAssetId: 'london-video-june-5-afternoon-two',
});
const londonVideoJune5EveningOne = mediaRef('adventures/london-video-2025-06-05-202238.mp4', {
  firebaseAssetId: 'london-video-june-5-evening-one',
});
const londonVideoJune5EveningTwo = mediaRef('adventures/london-video-2025-06-05-202320.mp4', {
  firebaseAssetId: 'london-video-june-5-evening-two',
});
const londonVideoJune5Night = mediaRef('adventures/london-video-2025-06-05-220254.mp4', {
  firebaseAssetId: 'london-video-june-5-night',
});

const legacy = galleryBySlug['sxsw-london-2025'];

const findLegacyStory = (id, sections = legacy.blog?.sections || []) => {
  const directStory = sections.find((section) => section.id === id);

  return directStory || sections.reduce((matchedStory, section) => (
    matchedStory || findLegacyStory(id, section.subsections || [])
  ), null);
};

const migratedLegacyStory = (id, storyTitle) => {
  const story = findLegacyStory(id);

  if (!story) return {};

  return {
    storyTitle: storyTitle || story.heading,
    storySections: [
      {
        heading: story.heading,
        paragraphs: story.paragraphs || [],
        ...(story.bullets?.length ? { items: story.bullets } : {}),
      },
    ],
  };
};

const reference = (entityId, order, dayLabel, eventDescription, extra = {}) => ({
  entityId,
  order,
  dayLabel,
  eventDescription,
  ...extra,
});

const datedOccurrence = (startDate, relationshipOrder, displayTime, extra = {}) => ({
  startDate,
  timezone: 'Europe/London',
  datePrecision: 'approximate',
  displayDate: new Date(`${startDate}T00:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  }),
  displayTime,
  sortTimestamp: extra.startTime ? `${startDate}T${extra.startTime}` : startDate,
  sequenceOverride: relationshipOrder,
  relationshipOrder,
  ...extra,
});

const mondayOccurrence = (order, time, extra = {}) => datedOccurrence('2025-06-02', order, time, extra);
const tuesdayOccurrence = (order, time, extra = {}) => datedOccurrence('2025-06-03', order, time, extra);
const wednesdayOccurrence = (order, time, extra = {}) => datedOccurrence('2025-06-04', order, time, extra);
const thursdayOccurrence = (order, time, extra = {}) => datedOccurrence('2025-06-05', order, time, extra);
const fridayOccurrence = (order, time, extra = {}) => datedOccurrence('2025-06-06', order, time, extra);
const saturdayOccurrence = (order, time, extra = {}) => datedOccurrence('2025-06-07', order, time, extra);

const cardImage = (id, src, alt, position = 'center center', orientation = 'landscape') => ({
  id,
  src,
  alt,
  position,
  orientation,
});

const chronologicalGallery = (items) => [...items].sort((left, right) => (
  left.capturedAt.localeCompare(right.capturedAt)
));

const sxswLondon2025 = defineReckonEvent({
  id: 'sxsw-london-2025',
  slug: 'sxsw-london-2025',
  title: 'SXSW London 2025',
  subtitle: 'Technology. Film. Music. One Neighborhood at a Time.',
  eventType: 'conference',
  status: 'published',
  startDate: '2025-06-02',
  endDate: '2025-06-07',
  year: 2025,
  destination: 'Shoreditch',
  chronologyTravelMode: 'walking',
  chronologyHotelsByDate: {
    '2025-06-02': 'nobu-shoreditch',
    '2025-06-03': 'nobu-shoreditch',
    '2025-06-04': 'nobu-shoreditch',
    '2025-06-05': 'nobu-shoreditch',
    '2025-06-06': 'hyatt-centric-dublin',
  },
  city: 'London',
  country: 'United Kingdom',
  heroImage: {
    id: 'sxsw-london-hero',
    src: legacy.heroImage,
    alt: 'Reckon heroes sharing a drink outside a London pub',
    orientation: 'landscape',
  },
  summary: legacy.summary,
  atAGlance: [
    {
      id: 'destination', label: 'Destination', value: 'London, United Kingdom', icon: 'mdi-map-marker-outline',
    },
    {
      id: 'dates', label: 'Dates', value: 'June 2–7, 2025', icon: 'mdi-calendar-range',
    },
    {
      id: 'crew', label: 'Crew', value: 2, suffix: ' travelers', icon: 'mdi-account-group-outline',
    },
    {
      id: 'home-base', label: 'Home Bases', value: 'Nobu Shoreditch + art’otel Hoxton + Hyatt Centric Dublin', icon: 'mdi-bed-outline',
    },
  ],
  tripByNumbers: [
    { id: 'time-zones', label: 'Time zones crossed', value: 5 },
    { id: 'films', label: 'Film premieres', value: 2 },
    { id: 'music', label: 'Live music events', value: 4 },
    { id: 'breweries', label: 'Breweries visited', value: 4 },
    { id: 'pubs', label: 'Pubs visited', value: '10+' },
  ],
  experienceDNA: [
    {
      id: 'food', label: 'Food', score: 80, icon: 'mdi-silverware-fork-knife',
    },
    {
      id: 'adventure', label: 'Adventure', score: 40, icon: 'mdi-compass-outline',
    },
    {
      id: 'family', label: 'Family', score: 0, icon: 'mdi-human-male-female-child',
    },
    {
      id: 'nightlife', label: 'Nightlife', score: 100, icon: 'mdi-music-note',
    },
    {
      id: 'nature', label: 'Nature', score: 0, icon: 'mdi-pine-tree',
    },
    {
      id: 'culture', label: 'Culture', score: 100, icon: 'mdi-theater',
    },
    {
      id: 'relaxation', label: 'Relaxation', score: 0, icon: 'mdi-weather-sunset',
    },
    {
      id: 'luxury', label: 'Luxury', score: 60, icon: 'mdi-star-outline',
    },
    {
      id: 'conference', label: 'Conference', score: 80, icon: 'mdi-presentation',
    },
  ],
  conference: {
    conferenceName: 'SXSW London',
    conferenceUrl: 'https://www.sxswlondon.com/',
    venueNames: ['The Truman Brewery', 'Shoreditch venues'],
    conferenceDates: 'June 2–7, 2025',
    purpose: legacy.purpose,
    attendanceReason: 'We wanted to learn from the inaugural event while deciding whether its mix of technology, film, music, and walkable city experiences could support a future full-crew trip.',
    memorableSessions: ['The View from 2050', 'The AI Voice Revolution', 'AI Agents: The Marketer’s New Best Friend?'],
  },
  journeyStrip: [
    { id: 'monday', label: 'Monday', value: 'Arrival, badge pickup, and opening night' },
    { id: 'tuesday', label: 'Tuesday', value: 'AI, film, and Shoreditch' },
    { id: 'wednesday', label: 'Wednesday', value: 'Sessions, live music, and premieres' },
    { id: 'thursday', label: 'Thursday', value: 'Tower Bridge and Bermondsey' },
    { id: 'friday', label: 'Friday', value: 'Early flight to Dublin, Brazen Head, Guinness Storehouse, and Temple Bar' },
  ],
  chronology: [
    {
      id: 'airport-taxi', title: 'Taxi from Airport', occurrence: mondayOccurrence(1, 'About 8:30 AM', { startTime: '08:30' }), evidence: 'Photo timestamp', note: 'Before hotel arrival',
    },
    {
      id: 'hotel-arrival', title: 'Hotel Arrival', occurrence: mondayOccurrence(2, 'About 9:50 AM', { startTime: '09:50' }), evidence: 'Marcus photo timestamp', note: 'After airport taxi and before badge pickup',
    },
    {
      id: 'badge-pickup', entityId: 'shoreditch-studios', title: 'SXSW London Badge Pickup — Shoreditch Studios', occurrence: mondayOccurrence(3, 'About 11:30 AM', { startTime: '11:30' }), evidence: 'Photo timestamp', note: 'After hotel arrival and before High Grade Coffee', mapTimeline: true, mapType: 'Registration', mapLocation: "Shoreditch Studios, 37 Bateman's Row, London EC2A 3HH, United Kingdom",
    },
    {
      id: 'high-grade-coffee-monday', title: 'High Grade Coffee', occurrence: mondayOccurrence(4, 'Late morning'), note: 'After badge pickup and before the SXSW London Expo',
    },
    {
      id: 'sxsw-london-expo', title: 'SXSW London Expo', occurrence: mondayOccurrence(5, 'Before 1:00 PM'), note: 'After High Grade Coffee and immediately before The View from 2050',
    },
    {
      id: 'the-view-from-2050', entityId: 'the-view-from-2050', title: 'The View from 2050 — Truman Brewery, SXSW London Stage', occurrence: mondayOccurrence(6, '1:00–1:35 PM', { startTime: '13:00', endTime: '13:35', datePrecision: 'exact' }), note: 'After the Expo and before heading to Village Underground', mapTimeline: true, mapType: 'Session', mapLocation: 'The Truman Brewery, SXSW London Stage, London, United Kingdom',
    },
    {
      id: 'henry-lau-queue', title: 'Waiting in Line for Henry Lau — Village Underground', occurrence: mondayOccurrence(7, 'Approximately 1:45/2:00–3:00 PM', { startTime: '13:45', endTime: '15:00' }), note: 'Waited at least 90 minutes before doors and entry',
    },
    {
      id: 'henry-lau-entry', title: 'Henry Lau Doors/Entry — Village Underground', occurrence: mondayOccurrence(8, 'About 3:00 PM', { startTime: '15:00' }), note: 'After the queue and before the performance',
    },
    {
      id: 'henry-lau-performance', title: 'Henry Lau — Village Underground', occurrence: mondayOccurrence(9, '4:00–5:00 PM', { startTime: '16:00', endTime: '17:00', datePrecision: 'exact' }), note: 'After the long queue and before The Queen’s Head',
    },
    {
      id: 'queens-head-monday', title: 'The Queen’s Head', occurrence: mondayOccurrence(10, 'Approximately 5:00–6:30 PM', { startTime: '17:00', endTime: '18:30' }), note: 'After Henry Lau and before the Festival Opening Party',
    },
    {
      id: 'festival-opening-party', title: 'Festival Opening Party — Truman Brewery, Brick Lane Yard', occurrence: mondayOccurrence(11, '6:30–8:30 PM', { startTime: '18:30', endTime: '20:30', datePrecision: 'exact' }), note: 'After The Queen’s Head and before Owl & Pussycat',
    },
    {
      id: 'owl-and-pussycat-monday', title: 'Owl & Pussycat', occurrence: mondayOccurrence(12, 'After 8:30 PM'), note: 'After the Festival Opening Party and before Old Street Records',
    },
    {
      id: 'old-street-records-monday', title: 'Old Street Records', occurrence: mondayOccurrence(13, 'Later evening'), note: 'After Owl & Pussycat',
    },
    {
      id: 'high-grade-coffee-tuesday', title: 'High Grade Coffee', occurrence: tuesdayOccurrence(1, 'Morning'), attendanceStatus: 'food',
    },
    {
      id: 'shoreditch-fish-chips-tuesday', title: 'Shoreditch Fish & Chips', occurrence: tuesdayOccurrence(2, 'Around noon', { startTime: '12:00' }), attendanceStatus: 'food',
    },
    {
      id: 'building-ai-products-queue', title: 'Queue at Shoreditch Electric', occurrence: tuesdayOccurrence(3, 'About 2:00 PM', { startTime: '14:00' }), attendanceStatus: 'attempted', note: 'Queued for Building AI Products',
    },
    {
      id: 'building-ai-products', entityId: 'building-ai-products', title: 'Building AI Products — Shoreditch Electric', occurrence: tuesdayOccurrence(4, '3:10–3:30 PM', { startTime: '15:10', endTime: '15:30', datePrecision: 'exact' }), attendanceStatus: 'missed', note: 'Missed because of the queue at Shoreditch Electric', mapTimeline: true, mapType: 'Attempted Session', mapLocation: 'Shoreditch Electric, Coronet Street, London N1 6HD, United Kingdom',
    },
    {
      id: 'rethinking-robotics', entityId: 'rethinking-robotics', title: 'Rethinking Robotics — Shoreditch Electric', occurrence: tuesdayOccurrence(5, 'From 4:10 PM', { startTime: '16:10' }), attendanceStatus: 'partial', note: 'Attended the beginning, left early, then went to meet Marcus at The Reliance', mapTimeline: true, mapType: 'Session', mapLocation: 'Shoreditch Electric, Coronet Street, London N1 6HD, United Kingdom',
    },
    {
      id: 'the-reliance-tuesday', title: 'The Reliance', occurrence: tuesdayOccurrence(6, 'Approximately 4:20–5:15 PM', { startTime: '16:20', endTime: '17:15' }), attendanceStatus: 'food', note: 'Met Marcus and ate; afterward walked roughly three blocks toward Curzon Hoxton',
    },
    {
      id: 'george-and-vulture-tuesday', title: 'George & Vulture', occurrence: tuesdayOccurrence(7, 'About 5:30 PM', { startTime: '17:30' }), attendanceStatus: 'food', evidence: 'Photo timestamp', note: 'After The Reliance and next to Curzon Hoxton',
    },
    {
      id: 'late-shift-curzon-hoxton', title: 'Late Shift — Curzon Hoxton, Screen 1', occurrence: tuesdayOccurrence(8, '7:20–9:17 PM', { startTime: '19:20', endTime: '21:17', datePrecision: 'exact' }), attendanceStatus: 'attended', note: 'Attended after George & Vulture',
    },

    {
      id: 'high-grade-coffee-wednesday', title: 'High Grade Coffee', occurrence: wednesdayOccurrence(1, 'Morning'), attendanceStatus: 'food',
    },
    {
      id: 'beigel-bake-wednesday', title: 'Beigel Bake', occurrence: wednesdayOccurrence(2, 'Around lunch'), attendanceStatus: 'food',
    },
    {
      id: 'ai-voice-revolution', entityId: 'ai-voice-revolution', title: 'The AI Voice Revolution — Truman Brewery', occurrence: wednesdayOccurrence(3, '12:45–1:10 PM', { startTime: '12:45', endTime: '13:10', datePrecision: 'exact' }), attendanceStatus: 'attended', mapTimeline: true, mapType: 'Session', mapLocation: 'The Truman Brewery, SXSW London Stage, London, United Kingdom',
    },
    {
      id: 'golden-heart-wednesday', title: 'The Golden Heart', occurrence: wednesdayOccurrence(4, 'Between 1:10 and 3:35 PM'), attendanceStatus: 'food', note: 'Stopped for a pint before AI Agents',
    },
    {
      id: 'ai-agents-marketer', entityId: 'ai-agents-marketer', title: 'AI Agents: The Marketer’s New Best Friend? — Truman Brewery', occurrence: wednesdayOccurrence(5, '3:35–4:00 PM', { startTime: '15:35', endTime: '16:00', datePrecision: 'exact' }), attendanceStatus: 'attended', mapTimeline: true, mapType: 'Session', mapLocation: 'The Truman Brewery, SXSW London Stage, London, United Kingdom',
    },
    {
      id: 'idris-elba-queue', title: 'Queue for Creativity as Capital for Change', occurrence: wednesdayOccurrence(6, 'Before 5:45 PM'), attendanceStatus: 'attempted',
    },
    {
      id: 'idris-elba-creativity-session', entityId: 'idris-elba-creativity-session', title: 'Creativity as Capital for Change — Truman Brewery', occurrence: wednesdayOccurrence(7, '5:45–6:30 PM', { startTime: '17:45', endTime: '18:30', datePrecision: 'exact' }), attendanceStatus: 'missed', note: 'The session filled before we could enter', mapTimeline: true, mapType: 'Attempted Session', mapLocation: 'The Truman Brewery, SXSW London Stage, London, United Kingdom',
    },
    {
      id: 'old-blue-last-wednesday', title: 'The Old Blue Last', occurrence: wednesdayOccurrence(8, 'After the failed Idris Elba entry'), attendanceStatus: 'food', note: 'Before Deep Cover at the Barbican',
    },
    {
      id: 'deep-cover-barbican', title: 'Deep Cover — Barbican Hall', occurrence: wednesdayOccurrence(9, '8:00–10:30 PM', { startTime: '20:00', endTime: '22:30', datePrecision: 'exact' }), attendanceStatus: 'attended',
    },
    {
      id: 'the-ks-hoxton-hall',
      title: 'The K’s — Hoxton Hall',
      occurrence: wednesdayOccurrence(10, '11:00 PM–midnight', {
        startTime: '23:00', endDate: '2025-06-05', endTime: '00:00', datePrecision: 'exact',
      }),
      attendanceStatus: 'attended',
    },
    {
      id: 'idris-xoyo',
      title: 'IDRIS — XOYO, Room 1',
      occurrence: thursdayOccurrence(0.1, 'Midnight–1:00 AM', {
        startTime: '00:00', endTime: '01:00', datePrecision: 'exact', narrativeDay: 'Wednesday night',
      }),
      attendanceStatus: 'attended',
    },
    {
      id: 'hunger-cure', title: 'Hunger Cure', occurrence: thursdayOccurrence(0.2, 'After 1:00 AM', { startTime: '01:00', narrativeDay: 'Wednesday night' }), attendanceStatus: 'food',
    },

    {
      id: 'high-grade-coffee-thursday', title: 'High Grade Coffee', occurrence: thursdayOccurrence(1, 'Morning'), attendanceStatus: 'food',
    },
    {
      id: 'agentic-ai-people-powered-future', entityId: 'agentic-ai-people-powered-future', title: 'Agentic AI & The People Powered Future — Truman Brewery', occurrence: thursdayOccurrence(2, '12:10–12:35 PM', { startTime: '12:10', endTime: '12:35', datePrecision: 'exact' }), attendanceStatus: 'attended', mapTimeline: true, mapType: 'Session', mapLocation: 'The Truman Brewery, Stage 2, London, United Kingdom',
    },
    {
      id: 'the-reliance-thursday', title: 'The Reliance', occurrence: thursdayOccurrence(3, 'After the session'), attendanceStatus: 'food', note: 'Second visit; before the walk toward Bermondsey',
    },
    {
      id: 'shoreditch-to-bermondsey-walk', title: 'Walk from Shoreditch toward Tower Bridge and Bermondsey', occurrence: thursdayOccurrence(4, 'Afternoon'), attendanceStatus: 'travel',
    },
    {
      id: 'tower-bridge-thursday', title: 'Tower Bridge', occurrence: thursdayOccurrence(5, 'Afternoon'), attendanceStatus: 'incidental',
    },
    {
      id: 'doodle-bar-thursday', title: 'Doodle Bar', occurrence: thursdayOccurrence(6, 'About 4:20 PM', { startTime: '16:20' }), attendanceStatus: 'food', note: 'First viable beer stop; drank London Black',
    },
    {
      id: 'barrel-project-thursday', title: 'The Barrel Project', occurrence: thursdayOccurrence(7, 'From about 4:50 PM', { startTime: '16:50' }), attendanceStatus: 'food',
    },
    {
      id: 'fabal-beerhall-thursday', title: 'Fabal Beerhall', occurrence: thursdayOccurrence(8, 'After The Barrel Project'), attendanceStatus: 'food',
    },
    {
      id: 'hiver-taproom-thursday', title: 'Hiver Taproom', occurrence: thursdayOccurrence(9, 'After Fabal Beerhall'), attendanceStatus: 'food',
    },
    {
      id: 'poulet-thursday', title: 'Poulet', occurrence: thursdayOccurrence(10, 'Around 10:00 PM', { startTime: '22:00' }), attendanceStatus: 'food',
    },
    {
      id: 'culpeper-thursday', title: 'The Culpeper', occurrence: thursdayOccurrence(11, 'Late evening'), attendanceStatus: 'food', note: 'After Poulet, stayed until last call near the midnight boundary',
    },

    {
      id: 'depart-london-friday', title: 'Departed London for Heathrow', occurrence: fridayOccurrence(1, 'Early morning'), attendanceStatus: 'travel', note: 'Marcus met Kevin at Nobu before the cab to Heathrow',
    },
    {
      id: 'flight-to-dublin', title: 'Flight from Heathrow to Dublin', occurrence: fridayOccurrence(2, 'Early morning'), attendanceStatus: 'travel',
    },
    {
      id: 'dublin-arrival', title: 'Arrived in Dublin and dropped bags at the hotel', occurrence: fridayOccurrence(3, 'Morning'), attendanceStatus: 'travel',
    },
    {
      id: 'st-patricks-pass', title: 'Walk past St Patrick’s Cathedral', occurrence: fridayOccurrence(4, 'Morning'), attendanceStatus: 'incidental', note: 'Passed on the walk; not a formal visit',
    },
    {
      id: 'brazen-head-dublin', title: 'The Brazen Head', occurrence: fridayOccurrence(5, 'Late morning'), attendanceStatus: 'food',
    },
    {
      id: 'guinness-storehouse-tour', title: 'Guinness Storehouse', occurrence: fridayOccurrence(6, 'Late morning to early afternoon'), attendanceStatus: 'attended', note: 'Self-guided visit with tastings, pint pouring, and the Gravity Bar',
    },
    {
      id: 'dublin-carriage', title: 'Horse-drawn carriage to the Temple Bar area', occurrence: fridayOccurrence(7, 'Afternoon'), attendanceStatus: 'travel',
    },
    {
      id: 'bad-bobs-temple-bar', title: 'Bad Bobs Temple Bar', occurrence: fridayOccurrence(8, 'Afternoon'), attendanceStatus: 'food', note: 'Traditional Irish food and live music; before The Temple Bar',
    },
    {
      id: 'temple-bar-dublin', title: 'The Temple Bar Pub', occurrence: fridayOccurrence(9, 'Later afternoon or evening'), attendanceStatus: 'food', note: 'After Bad Bobs Temple Bar',
    },
    {
      id: 'mercantile-hotel-dublin', title: 'The Mercantile Hotel', occurrence: fridayOccurrence(10, 'Evening'), attendanceStatus: 'food', note: 'Stopped on the walk back to the Dublin hotel',
    },
    {
      id: 'walk-to-dublin-hotel', title: 'Walk back to the Dublin hotel', occurrence: fridayOccurrence(11, 'Late evening'), attendanceStatus: 'travel',
    },
    {
      id: 'multi-pizza-dublin', title: 'Multi Pizza', occurrence: fridayOccurrence(12, 'Late evening'), attendanceStatus: 'food', note: 'Near the Dublin hotel',
    },

    {
      id: 'return-home-saturday', title: 'Return home', occurrence: saturdayOccurrence(1, 'Travel day'), attendanceStatus: 'travel', note: 'Kept as a minimal travel occurrence with no standalone narrative',
    },
  ],
  sessions: [
    reference('the-view-from-2050', 9, 'Monday', 'A panel of experts in AI, quantum technology, and entrepreneurship looked ahead to what the world could become and how we might get there.', {
      location: 'The Truman Brewery — SXSW London Stage',
      occurrence: mondayOccurrence(6, '1:00–1:35 PM', {
        startTime: '13:00', endTime: '13:35', datePrecision: 'exact',
      }),
    }),
    reference('building-ai-products', 10, 'Tuesday', 'We queued at Shoreditch Electric but missed Building AI Products because we could not get inside in time.', {
      location: 'Shoreditch Electric',
      occurrence: tuesdayOccurrence(4, '3:10–3:30 PM', { startTime: '15:10', endTime: '15:30', datePrecision: 'exact' }),
      attendanceStatus: 'missed',
      whatHappened: 'We queued outside Shoreditch Electric but missed the session because we could not get inside in time.',
    }),
    reference('rethinking-robotics', 11, 'Tuesday', 'We attended the beginning of Rethinking Robotics at Shoreditch Electric, then left early when it proved less relevant than expected.', {
      location: 'Shoreditch Electric',
      occurrence: tuesdayOccurrence(5, 'From 4:10 PM', { startTime: '16:10' }),
      attendanceStatus: 'partial',
      whatHappened: 'We attended the beginning of the session but left early, then went to meet Marcus at The Reliance.',
    }),
    reference('ai-voice-revolution', 20, 'Wednesday', 'We attended The AI Voice Revolution at the Truman Brewery.', {
      location: 'The Truman Brewery',
      occurrence: wednesdayOccurrence(3, '12:45–1:10 PM', { startTime: '12:45', endTime: '13:10', datePrecision: 'exact' }),
      attendanceStatus: 'attended',
    }),
    reference('ai-agents-marketer', 21, 'Wednesday', 'We attended AI Agents: The Marketer’s New Best Friend? after a pint at The Golden Heart.', {
      occurrence: wednesdayOccurrence(5, '3:35–4:00 PM', { startTime: '15:35', endTime: '16:00', datePrecision: 'exact' }),
      attendanceStatus: 'attended',
    }),
    reference('idris-elba-creativity-session', 22, 'Wednesday', 'We joined the queue for Idris Elba’s Creativity as Capital for Change session, but it stretched for multiple blocks.', {
      occurrence: wednesdayOccurrence(7, '5:45–6:30 PM', { startTime: '17:45', endTime: '18:30', datePrecision: 'exact' }),
      attendanceStatus: 'missed',
      whyWeWent: 'To hear Idris Elba discuss creativity as a force for business and social change.',
      whatHappened: 'Even with Platinum badges, the session filled before we could get inside.',
      whatWeRemember: 'Missing the session was disappointing, but it set up one of the best stories of the trip later that night at XOYO.',
      cardImage: cardImage('idris-elba-session-queue-card', idrisElbaSessionQueueImage, 'The Reckon crew standing in the long queue for Idris Elba’s SXSW London session', 'center 64%', 'portrait'),
    }),
    reference('agentic-ai-people-powered-future', 30, 'Thursday', 'We attended Agentic AI & The People Powered Future before returning to The Reliance.', {
      occurrence: thursdayOccurrence(2, '12:10–12:35 PM', { startTime: '12:10', endTime: '12:35', datePrecision: 'exact' }),
      attendanceStatus: 'attended',
    }),
  ],
  venues: [
    reference('sxsw-london', 1, 'Monday', 'The conference turned Shoreditch itself into the campus, with ideas and experiences spread across theaters, clubs, courtyards, and creative spaces.', {
      cardImage: cardImage('sxsw-london-sign-card', sxswLondonSignImage, 'Marcus and Kevin in front of the Welcome to SXSW London sign', 'center 44%', 'portrait'),
    }),
    reference('truman-brewery', 2, 'Monday', 'The former brewery complex served as a major SXSW London campus, bringing sessions, stages, lounges, food, and festival activity into one site.', {
      ...migratedLegacyStory('truman-brewery'),
      occurrence: mondayOccurrence(5, 'Before 1:00 PM'),
    }),
    reference('village-underground', 20, 'Monday', 'Henry Lau brought an international crowd together in one of the trip’s most energetic live performances.', {
      ...migratedLegacyStory('henry-lau-village-underground'),
      occurrence: mondayOccurrence(9, '4:00–5:00 PM', {
        startTime: '16:00', endTime: '17:00', datePrecision: 'exact',
      }),
      whatHappened: 'The queue wrapped around the block. We joined it around 1:45 or 2:00 PM and waited at least 90 minutes before entering around 3:00 PM for the 4:00 PM performance.',
      whatWeRemember: 'The crowd felt completely different from what we were used to, almost like a K-pop performance. I was not familiar with Henry Lau before the show, but I left impressed by his talent, musicianship, and showmanship.',
      cardImage: cardImage('henry-lau-village-underground-card', henryLauVillageUndergroundImage, 'Henry Lau playing violin at Village Underground during SXSW London 2025', 'center 53%', 'portrait'),
    }),
    reference('curzon-hoxton', 21, 'Tuesday', 'Late Shift and its filmmaker conversation made the audience feel unusually close to the creative process.', {
      ...migratedLegacyStory('late-shift-curzon-hoxton'),
      occurrence: tuesdayOccurrence(23, '7:20–9:17 PM', {
        startTime: '19:20', endTime: '21:17', datePrecision: 'exact',
      }),
      whatHappened: 'After the credits rolled, actress Leonie Benesch stood up from our row to participate in the audience Q&A.',
      whatWeRemember: 'We had no idea she had been sitting just a few seats away during the entire film. It was one of those uniquely SXSW moments where the distance between audience and creator disappeared.',
      cardImage: cardImage('late-shift-qa-card', lateShiftQaImage, 'The Late Shift post-screening filmmaker Q&A at Curzon Hoxton', 'center 62%', 'portrait'),
    }),
    reference('barbican-centre', 22, 'Wednesday', 'The Deep Cover premiere widened the week beyond technology and into London’s film culture.', {
      ...migratedLegacyStory('deep-cover-barbican-centre'),
      occurrence: wednesdayOccurrence(9, '8:00–10:30 PM', { startTime: '20:00', endTime: '22:30', datePrecision: 'exact' }),
      whatHappened: 'Before the movie started, we stood outside watching the red-carpet arrivals as Orlando Bloom, Bryce Dallas Howard, Nick Mohammed, and others entered the Barbican Centre.',
      whatWeRemember: 'Camera flashes lit up the entrance and made it feel like we had stepped into Hollywood for the evening. After the film, Orlando Bloom, Bryce Dallas Howard, and Nick Mohammed joined a live cast Q&A.',
      cardImage: cardImage('barbican-centre-card', barbicanCentreImage, 'The Deep Cover cast and filmmakers in conversation at the Barbican Centre', 'center 65%'),
    }),
    reference('the-ks-hoxton-hall', 22.5, 'Wednesday', 'We caught The K’s live at Hoxton Hall before walking on to XOYO.', {
      occurrence: wednesdayOccurrence(10, '11:00 PM–midnight', {
        startTime: '23:00', endDate: '2025-06-05', endTime: '00:00', datePrecision: 'exact',
      }),
      storyTitle: 'The K’s at Hoxton Hall',
      storySections: [{
        heading: 'The K’s at Hoxton Hall',
        paragraphs: ['After Deep Cover at the Barbican, we saw The K’s play Hoxton Hall from 11:00 PM until midnight, then continued to XOYO.'],
      }],
      cardImage: cardImage('the-ks-hoxton-hall-card', theKsHoxtonHallImage, 'The K’s performing live at Hoxton Hall during SXSW London 2025', 'center 62%', 'portrait'),
    }),
    reference('xoyo', 23, 'Wednesday', 'Idris Elba’s DJ set became the perfect ending to one of the most memorable conference days.', {
      ...migratedLegacyStory('xoyo-idris-elba-dj-set'),
      occurrence: thursdayOccurrence(0.1, 'Midnight–1:00 AM', {
        startTime: '00:00', endTime: '01:00', datePrecision: 'exact', narrativeDay: 'Wednesday night',
      }),
      whatWeRemember: 'Marcus nearly walked into Idris Elba, then missed Wyclef Jean too, while concentrating on which queue to join.',
      cardImage: cardImage('xoyo-card', xoyoDjImage, 'Idris Elba performing a DJ set at XOYO during SXSW London 2025', 'center 42%', 'portrait'),
    }),
    reference('old-street-records', 24, 'Late Monday', 'A nearby live music stop that kept the conference energy going after the scheduled program.', {
      ...migratedLegacyStory('old-street-records'),
      occurrence: mondayOccurrence(13, 'Later evening'),
    }),
  ],
  places: [
    reference('tower-bridge-walk', 40, 'Thursday', 'After the morning sessions, we walked to the Tower of London, crossed Tower Bridge, and continued toward the Bermondsey Beer Mile.', {
      ...migratedLegacyStory('tower-bridge-to-bermondsey-beer-mile'),
      occurrence: thursdayOccurrence(5, 'Afternoon'),
      whyWeWent: 'To trade the conference floor for a long city walk and see how easily London experiences could become part of a larger Reckon trip.',
      whatWeRemember: 'The route connected history, neighborhoods, and the next set of brewery stops without feeling like a formal itinerary.',
      cardImage: cardImage('tower-bridge-walk-card', towerBridgeWalkImage, 'Two members of the Reckon team at Tower Bridge during the walk to Bermondsey', 'center bottom', 'portrait'),
    }),
    reference('dublin-side-quest', 60, 'Friday', 'A quick early morning flight turned the final day into a bonus Dublin adventure.', {
      occurrence: fridayOccurrence(2, 'Early morning'),
      storyTitle: 'London to Dublin',
      storySections: [{
        heading: 'London to Dublin',
        paragraphs: ['Marcus met Kevin at Nobu before an early cab to Heathrow. The flight to Dublin turned Friday into a short extension of the London trip.'],
      }],
      whyWeWent: 'To make the most of being nearby and add an unexpected second city to the trip.',
      whatWeRemember: 'The short flight made Dublin feel like a natural extension of the London experience.',
      cardImage: cardImage('dublin-side-quest-card', dublinSideQuestImage, 'Two members of the Reckon team in St Patrick’s Park during the Dublin side quest', 'center 52%'),
      featured: true,
    }),
  ],
  activities: [
    reference('tower-bridge-walk', 40, 'Thursday', 'A city-scale team experience that turned the walk between landmarks and breweries into part of the conference story.', {
      ...migratedLegacyStory('tower-bridge-to-bermondsey-beer-mile'),
      occurrence: thursdayOccurrence(5, 'Afternoon'),
      relatedBadgeIds: ['bermondsey-beer-mile'],
      cardImage: cardImage('tower-bridge-walk-activity-card', towerBridgeWalkImage, 'Two members of the Reckon team at Tower Bridge during the walk to Bermondsey', 'center bottom', 'portrait'),
    }),
    reference('guinness-storehouse-tour', 61, 'Friday', 'We explored the story and craft behind Ireland’s most famous stout at the Guinness Storehouse.', {
      occurrence: fridayOccurrence(6, 'Late morning to early afternoon'),
      storyTitle: 'Guinness Storehouse',
      storySections: [{
        heading: 'Guinness Storehouse',
        paragraphs: ['We took the self-guided route through the Storehouse, stopped for tastings, learned to pour a pint, and finished in the Gravity Bar.'],
      }],
      relatedBadgeIds: ['historic-pub-crawl'],
      cardImage: cardImage('guinness-storehouse-card', guinnessStorehouseImage, 'The Reckon crew outside the Guinness Storehouse at St. James’s Gate in Dublin', 'center 52%', 'portrait'),
    }),
  ],
  restaurants: [
    reference('high-grade-coffee', 3, 'Monday–Thursday', 'It quickly became part of the daily rhythm before heading into SXSW sessions.', {
      ...migratedLegacyStory('high-grade-coffee'),
      occurrences: [
        mondayOccurrence(4, 'Late morning'),
        tuesdayOccurrence(1, 'Morning'),
        wednesdayOccurrence(1, 'Morning'),
        thursdayOccurrence(1, 'Morning'),
      ],
    }),
    reference('owl-and-pussycat', 4, 'Monday', 'We stopped at Owl & Pussycat after the SXSW London opening party and before finishing the night at Old Street Records.', {
      occurrence: mondayOccurrence(12, 'After 8:30 PM'),
    }),
    reference('shoreditch-fish-chips', 11, 'Tuesday', 'After a morning session, we walked to Shoreditch Fish & Chips for a classic London lunch.', {
      ...migratedLegacyStory('shoreditch-fish-and-chips'),
      occurrence: tuesdayOccurrence(2, 'Around noon', { startTime: '12:00' }),
      whatHappened: 'The fish was amazingly fresh and amazingly hot. The first bite burned the roof of my mouth, and we also tried mushy peas because when in London, you try the mushy peas.',
      whatWeRemember: 'Lesson learned: give the fish a few minutes before digging in.',
    }),
    reference('beigel-bake', 12, 'Wednesday', 'We stopped at Beigel Bake for a salt beef bagel around lunch on Wednesday.', {
      ...migratedLegacyStory('beigel-bake'),
      occurrence: wednesdayOccurrence(2, 'Around lunch'),
      whatHappened: 'The line stretched out the door, which was the only reason I went there in the first place. Nearly everyone ahead of us ordered the salt beef bagel, so I followed their lead.',
      whatWeRemember: 'No regrets. It became one of those meals I still think about longingly whenever I eat a Reuben back home.',
    }),
    reference('queens-head', 13, 'Monday', 'After Henry Lau, we stopped at The Queen’s Head for a pint and pub food before the Festival Opening Party.', {
      ...migratedLegacyStory('the-queens-head'),
      occurrence: mondayOccurrence(10, 'Approximately 5:00–6:30 PM', { startTime: '17:00', endTime: '18:30' }),
      whatHappened: 'We grabbed the empty stools by the front window so we could people watch while enjoying a pint.',
      whatWeRemember: 'It was the perfect way to slow down and reset between Henry Lau and the Festival Opening Party.',
      cardImage: cardImage('queens-head-card', queensHeadImage, 'Two pints of Guinness by the front window at The Queen’s Head in Shoreditch', 'center 58%', 'portrait'),
    }),
    reference('george-and-vulture', 14, 'Tuesday', 'The George & Vulture was next to Curzon Hoxton, where we saw the premiere of Late Shift.', {
      ...migratedLegacyStory('george-and-vulture'),
      occurrence: tuesdayOccurrence(22, 'About 5:30 PM', { startTime: '17:30' }),
      whatHappened: 'We stopped in for a pint before the movie. The inside was a beautiful old pub, but the weather was too good to ignore, so we sat outside at the picnic tables.',
      whatWeRemember: 'It gave us the perfect chance to enjoy the weather, people watch, and settle in before the film.',
      cardImage: cardImage('george-and-vulture-card', georgeAndVultureImage, 'A Fuller’s Frontier lager outside George & Vulture before the Late Shift premiere', 'center 50%', 'portrait'),
    }),
    reference('the-reliance', 15, 'Tuesday', 'The Reliance became our unofficial neighborhood pub.', {
      ...migratedLegacyStory('the-reliance'),
      occurrences: [
        tuesdayOccurrence(6, 'Approximately 4:20–5:15 PM', { startTime: '16:20', endTime: '17:15' }),
        thursdayOccurrence(3, 'After the session', { note: 'Second visit; before the walk toward Bermondsey' }),
      ],
      visitCount: 2,
      whatHappened: 'I met Marcus there after our separate sessions and we ate before walking roughly three blocks toward Curzon Hoxton. It was conveniently located between venues, so we used it as an impromptu meeting place. Marcus loved the Wagyu beef burger so much that we visited twice.',
      whatWeRemember: 'By the end of the week, The Reliance felt less like a random pub and more like our London meeting spot.',
      cardImage: cardImage('the-reliance-card', theRelianceImage, 'A Five Points Brewing Company pint at The Reliance during SXSW London 2025', 'center 43%', 'portrait'),
    }),
    reference('golden-heart', 15.5, 'Wednesday', 'We stopped at The Golden Heart for a pint between The AI Voice Revolution and AI Agents.', {
      occurrence: wednesdayOccurrence(4, 'Between 1:10 and 3:35 PM'),
      storyTitle: 'The Golden Heart',
      storySections: [{
        heading: 'The Golden Heart',
        paragraphs: ['We stopped for a pint after The AI Voice Revolution and before the afternoon AI Agents session.'],
      }],
    }),
    reference('old-blue-last', 16, 'Wednesday', 'We went to The Old Blue Last after failing to get into Idris Elba’s session and before Deep Cover at the Barbican.', {
      ...migratedLegacyStory('the-old-blue-last'),
      occurrence: wednesdayOccurrence(8, 'After the failed Idris Elba entry'),
    }),
    reference('doodle-bar', 40.5, 'Thursday', 'Doodle Bar was our first brewery stop after crossing Tower Bridge and reaching the Bermondsey Beer Mile.', {
      occurrence: thursdayOccurrence(6, 'About 4:20 PM', { startTime: '16:20' }),
      storyTitle: 'Doodle Bar',
      storySections: [{
        heading: 'Doodle Bar',
        paragraphs: ['Doodle Bar was the first viable beer stop after the walk to Bermondsey. We ordered London Black.'],
      }],
      cardImage: cardImage('doodle-bar-card', doodleBarImage, 'A dark pint inside Doodle Bar on the Bermondsey Beer Mile', 'center 48%', 'portrait'),
    }),
    reference('barrel-project', 41, 'Thursday', 'The Barrel Project was probably my favorite stop on the Bermondsey Beer Mile.', {
      ...migratedLegacyStory('the-barrel-project'),
      occurrence: thursdayOccurrence(7, 'From about 4:50 PM', { startTime: '16:50' }),
      whatHappened: 'The atmosphere made it memorable immediately. Sitting among walls lined with aging barrels gave the space a completely different feel from the other breweries.',
      whatWeRemember: 'It felt like the kind of place you only find by walking a little farther than planned.',
      cardImage: cardImage('barrel-project-card', barrelProjectImage, 'A London Beer Factory pint surrounded by aging barrels at The Barrel Project', 'center 43%', 'portrait'),
    }),
    reference('fabal-beerhall', 41.25, 'Thursday', 'Fabal Beerhall followed The Barrel Project on our walk along the Bermondsey Beer Mile.', {
      occurrence: thursdayOccurrence(8, 'After The Barrel Project'),
      storyTitle: 'Fabal Beerhall',
      storySections: [{
        heading: 'Fabal Beerhall',
        paragraphs: ['Fabal Beerhall was the next stop after The Barrel Project and before Hiver Taproom.'],
      }],
      whatWeRemember: 'The blue lighting, corrugated walls, and oversized Fabal Lager artwork gave it the most colorful, modern feel of the Beer Mile stops.',
      cardImage: cardImage('fabal-beerhall-card', fabalBeerhallImage, 'A pint beneath Fabal Lager artwork inside Fabal Beerhall', 'center 40%', 'portrait'),
    }),
    reference('hiver-taproom', 41.5, 'Thursday', 'Hiver Taproom followed Fabal Beerhall on the Bermondsey Beer Mile.', {
      occurrence: thursdayOccurrence(9, 'After Fabal Beerhall'),
      storyTitle: 'Hiver Taproom',
      storySections: [{
        heading: 'Hiver Taproom',
        paragraphs: ['We reached Hiver Taproom after Fabal Beerhall and before dinner at Poulet.'],
      }],
    }),
    reference('poulet-london', 42, 'Thursday', 'After walking nearly the entire Bermondsey Beer Mile, we ended the evening at a small neighborhood restaurant.', {
      ...migratedLegacyStory('poulet'),
      occurrence: thursdayOccurrence(10, 'Around 10:00 PM', { startTime: '22:00' }),
      whatHappened: 'The chef told us there was only one chicken left, which made the decision easy.',
      whatWeRemember: 'It became one of the best meals of the trip and the perfect end to the day.',
      recommendation: 'Reckon approved, especially when there is only one chicken left.',
      cardImage: {
        ...cardImage('poulet-card', pouletChickenImage, 'Roast chicken, potatoes, and salad served at Poulet in London', 'center 66%', 'portrait'),
        caption: 'The last chicken of the night was one of the best meals of the trip.',
      },
      featured: true,
    }),
    reference('the-culpeper', 43, 'Thursday', 'A final London pub stop that kept the walk back toward Shoreditch relaxed and conversational.', {
      ...migratedLegacyStory('the-culpeper'),
      occurrence: thursdayOccurrence(11, 'Late evening'),
      cardImage: cardImage('the-culpeper-card', culpeperImage, 'A final pint at The Culpeper after the Bermondsey Beer Mile', 'center 44%', 'portrait'),
    }),
    reference('hunger-cure', 23.5, 'Late Wednesday', 'A casual late-night meal that matched the spontaneous, keep-moving rhythm of the week.', {
      ...migratedLegacyStory('hunger-cure'),
      occurrence: thursdayOccurrence(0.2, 'After 1:00 AM', { startTime: '01:00', narrativeDay: 'Wednesday night' }),
    }),
    reference('brazen-head-dublin', 60, 'Friday', 'We stopped at The Brazen Head immediately after dropping our bags at the hotel.', {
      occurrence: fridayOccurrence(5, 'Late morning'),
      storyTitle: 'The Brazen Head',
      storySections: [{
        heading: 'The Brazen Head',
        paragraphs: ['After dropping our bags, we walked past St Patrick’s Cathedral toward The Brazen Head for the first Dublin stop.'],
      }],
      whatHappened: 'The first Dublin pint started almost as soon as the bags were down.',
      whatWeRemember: 'It was the quickest possible transition from an early flight to a proper Dublin side quest.',
      cardImage: cardImage('brazen-head-card', brazenHeadImage, 'A pint of Guinness in The Brazen Head courtyard in Dublin', 'center 42%', 'portrait'),
      featured: true,
    }),
    reference('bad-bobs-temple-bar', 61, 'Friday', 'The first stop in the Temple Bar area was Bad Bobs for traditional Irish food and live music.', {
      occurrence: fridayOccurrence(8, 'Afternoon'),
      storyTitle: 'Bad Bobs Temple Bar',
      storySections: [{
        heading: 'Bad Bobs Temple Bar',
        paragraphs: ['After the carriage ride into the Temple Bar area, we stopped at Bad Bobs for traditional Irish food and live music before continuing to The Temple Bar Pub.'],
      }],
      whatHappened: 'We ate, listened to live music, and then continued through the Temple Bar area.',
      whatWeRemember: 'Bad Bobs was the first of the two Temple Bar-area stops.',
    }),
    reference('temple-bar-dublin', 62, 'Friday', 'After Bad Bobs, we stopped at The Temple Bar Pub.', {
      occurrence: fridayOccurrence(9, 'Later afternoon or evening'),
      relatedBadgeIds: ['historic-pub-crawl'],
      storyTitle: 'The Temple Bar Pub',
      storySections: [{
        heading: 'The Temple Bar Pub',
        paragraphs: ['After Bad Bobs, we continued through the neighborhood and stopped at The Temple Bar Pub.'],
      }],
      whatHappened: 'We stopped at The Temple Bar Pub after Bad Bobs.',
      whatWeRemember: 'It was the second of the two Temple Bar-area pub stops.',
      cardImage: cardImage('temple-bar-card', templeBarImage, 'The Reckon crew inside The Temple Bar Pub in Dublin', 'center 42%', 'portrait'),
    }),
    reference('mercantile-hotel-dublin', 62.5, 'Friday', 'We stopped at The Mercantile Hotel on the walk back from the Temple Bar area.', {
      occurrence: fridayOccurrence(10, 'Evening'),
      storyTitle: 'The Mercantile Hotel',
      storySections: [{
        heading: 'The Mercantile Hotel',
        paragraphs: ['After The Temple Bar Pub, we stopped at The Mercantile Hotel on the walk back toward our Dublin hotel.'],
      }],
      whatHappened: 'We made one more stop at The Mercantile Hotel before continuing toward the Hyatt.',
      whatWeRemember: 'It came between The Temple Bar Pub and the final walk back to the hotel.',
    }),
    reference('multi-pizza-dublin', 63, 'Friday', 'A late-night pizza stop near the hotel closed out the Dublin side quest.', {
      occurrence: fridayOccurrence(12, 'Late evening'),
      storyTitle: 'Multi Pizza',
      storySections: [{
        heading: 'Multi Pizza',
        paragraphs: ['After walking back toward the Dublin hotel, we stopped at Multi Pizza for a late-night meal.'],
      }],
      whatHappened: 'We stopped for pizza near the hotel at the end of the night.',
      whatWeRemember: 'It was the final food stop of the Dublin side quest.',
    }),
  ],
  hotels: [
    reference('nobu-shoreditch', 1, 'Monday–Thursday', 'Nobu put us close to the action without making the trip feel boxed into the conference.', {
      ...migratedLegacyStory('nobu-london-shoreditch', 'Home Base'),
      whyWeWent: 'The walkable location made it possible to move between sessions, meals, music, and spontaneous neighborhood discoveries.',
      whatWeRemember: 'It became a reliable reset point before the next part of each long day.',
      featured: true,
    }),
    reference('artotel-london-hoxton', 2, 'Monday–Thursday', 'Marcus stayed at art’otel London Hoxton during the London leg of the trip.', {
      featured: true,
      relatedMediaIds: ['london-video-june-5-afternoon-one'],
    }),
    reference('hyatt-centric-dublin', 60, 'Friday night', 'Hyatt Centric The Liberties Dublin gave the side quest a home base near the day’s stops.', {
      whyWeWent: 'To stay close to the Dublin experiences we wanted to fit into a very short visit.',
      whatWeRemember: 'Dropping the bags and heading immediately to The Brazen Head set the pace for the rest of the day.',
      featured: true,
    }),
  ],
  crew: legacy.attendees,
  meritBadges: legacy.achievements,
  gallery: chronologicalGallery([
    ...legacy.media.map((item, index) => ({
      id: `sxsw-london-media-${index + 1}`,
      src: item.src,
      alt: item.alt,
      caption: item.caption,
      orientation: 'landscape',
      capturedAt: '2025-06-02T00:00:00',
    })),
    {
      ...cardImage('xoyo-idris-elba', xoyoDjImage, 'Idris Elba performing a DJ set at XOYO during SXSW London 2025', 'center 42%', 'portrait'),
      caption: 'Idris Elba in the DJ booth.',
      capturedAt: '2025-06-05T00:15:00',
    },
    {
      ...cardImage('xoyo-wyclef-jean', xoyoWyclefImage, 'Wyclef Jean at XOYO during SXSW London 2025'),
      caption: 'Wyclef Jean watching Elba’s set.',
      capturedAt: '2025-06-05T00:20:00',
    },
    {
      ...cardImage('poulet-beer-foreground', pouletBeerImage, 'A Stone & Wood beer in front of the chicken dinner at Poulet', 'center 48%', 'portrait'),
      caption: 'The original view of dinner, with the beer demanding the foreground.',
      capturedAt: '2025-06-05T21:49:48',
    },
    {
      ...cardImage('curzon-hoxton-exterior', curzonHoxtonExteriorImage, 'Curzon Hoxton cinema during SXSW London 2025', 'center 45%', 'portrait'),
      caption: 'Curzon Hoxton, home of the Late Shift premiere and filmmaker Q&A.',
      capturedAt: '2025-06-03T19:45:00',
    },
    {
      ...cardImage('london-flight-arrival', londonFlightArrivalImage, 'View from the airplane while arriving in London', 'center center', 'portrait'),
      caption: 'The approach into London at the start of the SXSW scouting trip.',
      capturedAt: '2025-06-02T07:34:51',
    },
    {
      ...cardImage('london-sxsw-entry', londonSxswEntryImage, 'Delegates entering the SXSW London campus in Shoreditch', 'center center', 'portrait'),
      caption: 'Joining the flow of delegates on the inaugural SXSW London campus.',
      capturedAt: '2025-06-02T11:35:02',
    },
    {
      ...cardImage('london-street-art-pink-bear', londonStreetArtPinkBearImage, 'Large pink bear street-art mural in Shoreditch', 'center center', 'portrait'),
      caption: 'Shoreditch street art became part of the conference walk between venues.',
      capturedAt: '2025-06-02T11:29:05',
    },
    {
      ...cardImage('london-sxsw-map', londonSxswMapImage, 'SXSW London 2025 festival campus map', 'center center'),
      caption: 'The festival map showed how conference, music, film, and arts venues spread across Shoreditch.',
      capturedAt: '2025-06-02T12:45:41',
    },
    {
      ...cardImage('london-xoyo-entrance', londonXoyoEntranceImage, 'XOYO London entrance during SXSW London', 'center center', 'portrait'),
      caption: 'Outside XOYO before one of the trip’s most memorable late-night music events.',
      capturedAt: '2025-06-03T23:33:58',
    },
    {
      ...cardImage('london-tower-bridge-reckon-bottle', londonTowerBridgeReckonBottleImage, 'Reckon water bottle photographed in front of Tower Bridge', 'center center', 'portrait'),
      caption: 'A Reckon bottle found its landmark photo at Tower Bridge.',
      capturedAt: '2025-06-05T15:50:14',
    },
    {
      ...cardImage('london-doodle-bar-sign', londonDoodleBarSignImage, 'Doodle Bar sign on Druid Street in Bermondsey', 'center center', 'portrait'),
      caption: 'Doodle Bar marked the beginning of the Bermondsey Beer Mile stops.',
      capturedAt: '2025-06-05T16:21:16',
    },
    {
      ...cardImage('london-red-phone-box', londonRedPhoneBoxImage, 'Weathered red telephone box on a Shoreditch street', 'center center', 'portrait'),
      caption: 'A classic London icon encountered while walking between SXSW venues.',
      capturedAt: '2025-06-05T15:28:24',
    },
    {
      ...cardImage('london-barrel-project-interior', londonBarrelProjectInteriorImage, 'Rows of aging barrels inside The Barrel Project', 'center center', 'portrait'),
      caption: 'The Barrel Project’s walls of aging barrels gave the Beer Mile stop its distinctive atmosphere.',
      capturedAt: '2025-06-05T16:49:23',
    },
    {
      ...cardImage('london-bermondsey-flags', londonBermondseyFlagsImage, 'International flags hanging above the Bermondsey Beer Mile', 'center center', 'portrait'),
      caption: 'International flags and string lights along the Bermondsey Beer Mile.',
      capturedAt: '2025-06-05T21:45:02',
    },
    {
      ...cardImage('london-poulet-dining-room', londonPouletDiningRoomImage, 'Colorful dining room at Poulet in London', 'center center', 'portrait'),
      caption: 'Inside Poulet while waiting for the last chicken of the night.',
      capturedAt: '2025-06-05T22:18:13',
    },
    {
      ...cardImage('london-tower-bridge-night', londonTowerBridgeNightImage, 'Tower Bridge illuminated at night with a red double-decker bus', 'center center', 'portrait'),
      caption: 'Tower Bridge lit up as a double-decker bus crossed at night.',
      capturedAt: '2025-06-05T22:49:11',
    },
    {
      ...cardImage('london-tower-bridge-panorama', londonTowerBridgePanoramaImage, 'Panoramic nighttime view beneath Tower Bridge', 'center center'),
      caption: 'A panoramic nighttime view from beneath Tower Bridge.',
      contain: true,
      capturedAt: '2025-06-05T22:50:38',
    },
    {
      id: 'london-video-june-2-morning',
      type: 'video',
      src: londonVideoJune2Morning,
      mimeType: 'video/mp4',
      caption: 'Arriving at the SXSW London campus on Monday morning.',
      capturedAt: '2025-06-02T09:51:02',
    },
    {
      id: 'london-video-june-2-afternoon',
      type: 'video',
      src: londonVideoJune2Afternoon,
      mimeType: 'video/mp4',
      caption: 'An opening-day moment from SXSW London in Shoreditch.',
      capturedAt: '2025-06-02T15:31:54',
    },
    {
      id: 'london-video-june-4-afternoon-one',
      type: 'video',
      src: londonVideoJune4AfternoonOne,
      mimeType: 'video/mp4',
      caption: 'A midweek scene from the SXSW London campus.',
      capturedAt: '2025-06-04T14:12:17',
    },
    {
      id: 'london-video-june-4-afternoon-two',
      type: 'video',
      src: londonVideoJune4AfternoonTwo,
      mimeType: 'video/mp4',
      caption: 'Moving between Wednesday’s conference experiences in Shoreditch.',
      capturedAt: '2025-06-04T14:39:34',
    },
    {
      id: 'london-video-june-4-night',
      type: 'video',
      src: londonVideoJune4Night,
      mimeType: 'video/mp4',
      caption: 'Wednesday night during SXSW London’s live-music program.',
      capturedAt: '2025-06-04T22:10:11',
    },
    {
      id: 'london-video-june-5-afternoon-one',
      type: 'video',
      src: londonVideoJune5AfternoonOne,
      mimeType: 'video/mp4',
      caption: 'A look inside Marcus’s room at art’otel London Hoxton.',
      capturedAt: '2025-06-05T14:22:49',
    },
    {
      id: 'london-video-june-5-afternoon-two',
      type: 'video',
      src: londonVideoJune5AfternoonTwo,
      mimeType: 'video/mp4',
      caption: 'Crossing Tower Bridge on the way to Bermondsey.',
      capturedAt: '2025-06-05T15:28:52',
    },
    {
      id: 'london-video-june-5-evening-one',
      type: 'video',
      src: londonVideoJune5EveningOne,
      mimeType: 'video/mp4',
      caption: 'Thursday evening on the Bermondsey Beer Mile.',
      capturedAt: '2025-06-05T20:22:38',
    },
    {
      id: 'london-video-june-5-evening-two',
      type: 'video',
      src: londonVideoJune5EveningTwo,
      mimeType: 'video/mp4',
      caption: 'Another moment from the Beer Mile as the London day continued.',
      capturedAt: '2025-06-05T20:23:20',
    },
    {
      id: 'london-video-june-5-night',
      type: 'video',
      src: londonVideoJune5Night,
      mimeType: 'video/mp4',
      caption: 'Closing out Thursday night in London.',
      capturedAt: '2025-06-05T22:02:54',
    },
  ]),
  tags: legacy.tags,
  closingReflection: 'SXSW London gave us exactly what we hoped for and more than we expected: new ideas, unforgettable venues, long walks, great meals, and a promising answer to whether London could work for a future Reckon Crew adventure. A quick Dublin side quest added one more reminder to leave room for the unexpected. The destinations were the backdrop. The shared curiosity became the story. That’s the Reckon Way.',
});

const imageBearingSections = ['sessions', 'venues', 'places', 'restaurants', 'hotels', 'activities'];
const pageImages = [
  { image: sxswLondon2025.heroImage, caption: sxswLondon2025.subtitle },
  ...imageBearingSections.flatMap((section) => sxswLondon2025[section].map((item) => ({
    image: item.cardImage,
    caption: item.eventDescription,
  }))),
];
const galleryBySource = new Map(sxswLondon2025.gallery.map((image) => [image.src, image]));

pageImages.forEach(({ image, caption }) => {
  if (!image || galleryBySource.has(image.src)) return;
  galleryBySource.set(image.src, { ...image, caption: image.caption || caption });
});

sxswLondon2025.gallery = [...galleryBySource.values()];

export default sxswLondon2025;
